import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiUrl } from '../../utils/api';
import {
  BuildingOfficeIcon,
  EyeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface Property {
  _id: string;
  numero: string;
  tipo: string;
  descripcion: string;
  capacidad: number;
  precioBase: number;
  estado: string;
  piso: number;
  amenidades: string[];
  propietarios: Array<{
    usuario: {
      _id: string;
      nombre: string;
      apellido: string;
    };
    porcentajePropiedad: number;
  }>;
}

const Properties: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl('/api/properties/my-properties'));
      setProperties(response.data.data.properties || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar las propiedades');
      setProperties([]); // Asegurar que properties sea un array vacío en caso de error
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getTypeLabel = (type: string) => {
    const typeLabels = {
      habitacion: 'Habitación',
      apartamento: 'Apartamento',
      suite: 'Suite'
    };
    return typeLabels[type as keyof typeof typeLabels] || type;
  };

  const displayProperties = Array.isArray(properties) ? properties : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hotel-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
        <button
          onClick={fetchProperties}
          className="mt-6 btn-primary"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
        <h2 className="text-2xl font-semibold text-gray-600">Mis Propiedades</h2>
        <p className="mt-1 text-sm text-gray-500">
          Gestiona y monitorea el estado de tus habitaciones y apartamentos
        </p>
      </div>

      {/* Tabla de propiedades */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Propiedad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Precio Base
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Propiedad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayProperties.map((property) => (
                <tr key={property._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-lg bg-hotel-100 flex items-center justify-center">
                          <BuildingOfficeIcon className="h-6 w-6 text-hotel-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          Habitación {property.numero}
                        </div>
                        <div className="text-sm text-gray-500">
                          Piso {property.piso} • {property.capacidad} personas
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {getTypeLabel(property.tipo)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(property.precioBase)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {property.propietarios.map((prop, index) => (
                        <div key={prop.usuario._id}>
                          {prop.usuario.nombre} {prop.usuario.apellido} ({prop.porcentajePropiedad}%)
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      className="text-hotel-600 hover:text-hotel-900 flex items-center"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      Ver detalles
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {displayProperties.length === 0 && (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No se encontraron propiedades</h3>
            <p className="mt-1 text-sm text-gray-500">
              Aún no tienes propiedades asignadas en el portal.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Properties;
